import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileUpload, faTimes } from "@fortawesome/free-solid-svg-icons"; // Added faTimes
import { saveOfferAndPromotions } from "./OfferAndPromotionsapi";
import Swal from "sweetalert2";

function OffersAndPromotions() {
  const { t } = useTranslation();

  const [alertType, setAlertType] = useState("image");
  const [caption, setCaption] = useState("chatBot");

  const [uploadedFile, setUploadedFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [publishError, setPublishError] = useState("");
  const [captionError, setCaptionError] = useState("");
  const [fileError, setFileError] = useState("");

  // Lock inputs after first upload
  const [isUploadLocked, setIsUploadLocked] = useState(false);

  // Disable buttons after successful actions
  const [isPublished, setIsPublished] = useState(false);
  const [isDraftSaved, setIsDraftSaved] = useState(false);

  const handleCaptionChange = (event) => {
    setCaption(event.target.value);
    setCaptionError("");
  };

  const handleAlertTypeChange = (event) => {
    setAlertType(event.target.value);
    setFileError("");
  };

  // ---- IMAGE HANDLER ----
  const handleImageFileChange = (event) => {
    if (isUploadLocked) return;
    const file = event.target.files[0];
    setFileError("");
    if (file) {
      setUploadedFile(file);
      setIsUploadLocked(true);
    }
  };

  // ---- VIDEO HANDLER ----
  const handleVideoFileChange = (event) => {
    if (isUploadLocked) return;
    const file = event.target.files[0];
    setFileError("");
    if (file) {
      setVideoFile(file);
      setIsUploadLocked(true);
    }
  };

  // ---- DESELECT HANDLER ----
const handleDeselectFile = () => {
  setUploadedFile(null);
  setVideoFile(null);
  setIsUploadLocked(false);
  setFileError("");
  setIsPublished(false);
  setIsDraftSaved(false);
};


  const formatBytes = (bytes) => {
    if (bytes === 0) return `0 ${t("bytes")}`;
    const k = 1024;
    const sizes = [t("bytes"), t("kb"), t("mb"), t("gb"), t("tb")];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // ---- DRAFT ----
  const handleDraft = async () => {
    if (isDraftSaved) return; // prevent multiple draft saves

    if (!caption) {
      setCaptionError(t("captionRequired"));
      return;
    }

    if (
      (alertType === "image" && !uploadedFile) ||
      (alertType === "video" && !videoFile)
    ) {
      setFileError(
        alertType === "image"
          ? t("uploadImageToSaveDraft")
          : t("selectFileToUpload")
      );
      return;
    }

    try {
      setIsSubmitting(true);
      await saveOfferAndPromotions(uploadedFile, videoFile, caption, "", "");
      alert("Draft saved successfully!");
      setIsDraftSaved(true); // lock draft save
    } catch (err) {
      console.error(err);
      setPublishError("Error saving draft. Try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---- PUBLISH ----
  const handlePublish = async () => {
    if (isPublished) return; // prevent multiple publishes

    if (
      (alertType === "image" && !uploadedFile) ||
      (alertType === "video" && !videoFile)
    ) {
      setFileError(
        alertType === "image"
          ? t("uploadImageToContinue")
          : t("selectFileToUpload")
      );
      return;
    }

    try {
        // Show SweetAlert loader before starting upload
        Swal.fire({
          title: "Publishing...",
          text:
            alertType === "image"
              ? "Please wait while your image is being uploaded."
              : "Please wait while your video is being uploaded.",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });
      setIsSubmitting(true);
      // Call your API
      await saveOfferAndPromotions(uploadedFile, videoFile, caption, "", "");
    Swal.fire({
      title: "Published Successfully!",
      text:
        alertType === "image"
          ? "Your image promotion has been published successfully."
          : "Your video promotion has been published successfully.",
      icon: "success",
      confirmButtonColor: "#171c8f",
      confirmButtonText: "OK",
    });
     // lock continue button
         // ✅ Reset all locks to allow next upload without reload
         setUploadedFile(null);
         setVideoFile(null);
         setIsUploadLocked(false);
         setIsPublished(false);
         setIsDraftSaved(false);
         setFileError("");
    } catch (err) {
      console.error(err);
      setPublishError("Error publishing. Try again.");
          Swal.fire({
            title: "Error!",
            text: "Something went wrong while publishing. Please try again.",
            icon: "error",
            confirmButtonColor: "#171c8f",
          });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isContinueDisabled = (!uploadedFile && !videoFile) || isPublished;
  const isDraftDisabled = (!uploadedFile && !videoFile) || isDraftSaved;

  return (
    <main
      style={{ padding: "20px", fontFamily: "sans-serif", color: "#374151" }}
    >
      <h2 style={{ fontWeight: "normal", marginBottom: "16px" }}>
        {t("offersAndPromotions")}
      </h2>

      <section
        style={{
          backgroundColor: "#ffffff",
          border: "1px solid #407edb",
          borderRadius: "0.375rem",
          padding: "24px",
        }}
      >
        {/* Caption Dropdown */}
        <div className="mb-3">
          <label className="form-label text-secondary">
            {t("offersAndPromotionCaption")}
          </label>
          <select
            style={{ fontSize: "14px", height: "48px" }}
            className="form-select"
            value={caption}
            onChange={handleCaptionChange}
            disabled={isUploadLocked}
          >
            <option value="chatBot">{t("chatBot")}</option>
            <option value="newOffer">{t("newOffer")}</option>
            <option value="limitedTimePromotion">
              {t("limitedTimePromotion")}
            </option>
          </select>
          {captionError && (
            <p className="text-danger small">{captionError}</p>
          )}
        </div>

        {/* Alert Type Dropdown */}
        <div className="mb-3">
          <label className="form-label text-secondary">
            {t("Upload Promotion Type")}
          </label>
          <select
            className="form-select"
            value={alertType}
            onChange={handleAlertTypeChange}
            disabled={isUploadLocked}
          >
            <option value="image">{t("images")}</option>
            <option value="video">{t("videos")}</option>
          </select>
        </div>

        {/* Upload Section */}
        {!isUploadLocked ? (
          alertType === "image" ? (
            <input
              type="file"
              id="image-upload"
              accept="*/*"
              onChange={handleImageFileChange}
            />
          ) : (
            <input
              type="file"
              id="video-upload"
              accept="*/*"
              onChange={handleVideoFileChange}
            />
          )
        ) : (
          <div
            style={{
              marginTop: "16px",
              padding: "10px",
              border: "1px solid #ccc",
              borderRadius: "8px",
            }}
          >
            <p>
              <strong>Uploaded File:</strong>
            </p>

            {/* File name + size + cross icon */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span>
                {uploadedFile ? uploadedFile.name : videoFile?.name} (
                {formatBytes(
                  uploadedFile ? uploadedFile.size : videoFile?.size
                )}
                )
              </span>
              <FontAwesomeIcon
                icon={faTimes}
                onClick={handleDeselectFile}
                style={{
                  color: "red",
                  cursor: "pointer",
                  fontSize: "16px",
                }}
                title="Remove file"
              />
            </div>

            <p style={{ color: "red", marginTop: "5px" }}>
              Upload Locked: You cannot upload another file.
            </p>
          </div>
        )}

        {fileError && <p className="text-danger small">{fileError}</p>}
        {publishError && <p className="text-danger small">{publishError}</p>}

        {/* Buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "32px",
            gap: "16px",
          }}
        >
         {false &&( <button
            type="button"
            onClick={handleDraft}
            disabled={isDraftDisabled}
            style={{
              border: "1px solid #171c8f",
              color: "#171c8f",
              borderRadius: "0.25rem",
              padding: "8px 16px",
              fontWeight: "bold",
              backgroundColor: isDraftDisabled ? "#999" : "transparent",
              cursor: isDraftDisabled ? "not-allowed" : "pointer",
            }}
          >
            {t("draft")}
          </button>)}

          <button
            type="submit"
            onClick={handlePublish}
            disabled={isContinueDisabled}
            style={{
              borderStyle: "hidden",
              backgroundColor: isContinueDisabled ? "#999" : "#171c8f",
              color: "#ffffff",
              borderRadius: "0.25rem",
              padding: "8px 20px",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              cursor: isContinueDisabled ? "not-allowed" : "pointer",
            }}
          >
            <span>{t("continue")}</span> →
          </button>
        </div>
      </section>
    </main>
  );
}

export default OffersAndPromotions;